// deps
import React, { ReactElement, useContext, useMemo, useRef } from "react";

// internal-dep
import {
    CartOverlayActionProvider,
    CheckoutActionTypes,
    CheckoutContext,
    CheckoutRoute,
} from "@swiggy-private/checkout-core";
import { RouteContext, RouteProps, ScreenTransition } from "@swiggy-private/core-ui";
import { RouteContextValue } from "@swiggy-private/core-ui";

// components
import { CartContextProvider } from "@cart-client/components/cart-context-provider";
import { GiftingCartActionProvider } from "@cart-client/components/cart-action-provider/GiftingCartActionProvider";
import { CartActionProvider } from "@cart-client/components/cart-action-provider";
import { GenericPaymentAlert } from "@cart-client/components/generic-alert";
import { CartAddressContextProvider } from "@cart-client/components/cart-address-context-provider/CartAddressContextProvider";
import { CrossSellM2ContextProvider } from "@cart-client/components/cross-sell-m2-context-provider/CrossSellM2ContextProvider";
import { CartToastContextProvider } from "@cart-client/components/cart-toast-context-provider/CartToastContextProvider";
import { CartIPLSwiggySixesProviderWrapper } from "@cart-client/components/ipl-swiggy-sixes-provider-wrapper";
import { AnalyticsOverrideProvider } from "@cart-client/components/analytics-override-provider/AnalyticsOverrideProvider";
import { CookingInstructionsFocusContextProvider } from "@cart-client/components/cooking-instructions-focus-context-provider";
import { FreeCashRemovalPopupContextProvider } from "@cart-client/components/free-cash-removal-popup-context-provider/FreeCashRemovalPopupContextProvider";
import { PnrDataContextProvider } from "@cart-client/components/pnr-data-context-provider/PnrDataContextProvider";

// helpers
import { CartFlowContextProvider } from "@cart-client/components/cart-flow-context-provider";
import { getCartSource } from "@cart-client/components/cart-main/helpers";

// constants
import { INTERNAL_CART_SOURCE } from "@cart-client/interfaces/Cart";

export function CartMain(props: RouteProps<CheckoutRoute>): ReactElement {
    const { checkoutState } = useContext(CheckoutContext);
    const { isForward } = useContext(RouteContext) as RouteContextValue<CheckoutRoute>;
    const cartInternalSourceRef = useRef<INTERNAL_CART_SOURCE>(getCartSource(checkoutState));

    const children =
        Array.isArray(props.children) && props.children.length === 1 ? (
            <ScreenTransition forward={isForward}>{props.children[0]}</ScreenTransition>
        ) : (
            props.children
        );

    const DynamicCartActionProvider = useMemo(() => {
        switch (cartInternalSourceRef.current) {
            case INTERNAL_CART_SOURCE.GIFTING:
                return GiftingCartActionProvider;
            default:
                return CartActionProvider;
        }
    }, []);

    return (
        <CartFlowContextProvider>
            <AnalyticsOverrideProvider>
                <CartContextProvider>
                    <CartOverlayActionProvider>
                        <CrossSellM2ContextProvider>
                            <CartToastContextProvider>
                                <DynamicCartActionProvider>
                                    <CartAddressContextProvider>
                                        <CartIPLSwiggySixesProviderWrapper>
                                            <CookingInstructionsFocusContextProvider>
                                                <FreeCashRemovalPopupContextProvider>
                                                    <PnrDataContextProvider>
                                                        {children}
                                                        {checkoutState.type ===
                                                            CheckoutActionTypes.GENERIC_PAYMENT_ALERT && (
                                                            <GenericPaymentAlert {...checkoutState.payload} />
                                                        )}
                                                    </PnrDataContextProvider>
                                                </FreeCashRemovalPopupContextProvider>
                                            </CookingInstructionsFocusContextProvider>
                                        </CartIPLSwiggySixesProviderWrapper>
                                    </CartAddressContextProvider>
                                </DynamicCartActionProvider>
                            </CartToastContextProvider>
                        </CrossSellM2ContextProvider>
                    </CartOverlayActionProvider>
                </CartContextProvider>
            </AnalyticsOverrideProvider>
        </CartFlowContextProvider>
    );
}
